import { styled, keyframes } from '@mui/system'

const DASH = 20
const CYCLE = '2400ms'
const STAGGER = 120 // ms

const drawStroke = keyframes`
  0%   { stroke-dashoffset: ${DASH}; }
  25%  { stroke-dashoffset: 0; }
  85%  { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: ${DASH}; }
`

const fillIn = keyframes`
  0%, 15%   { fill-opacity: 0; }
  30%, 85%  { fill-opacity: 1; }
  100%      { fill-opacity: 0; }
`

const colorPulse = keyframes`
  0%, 100% { stroke: var(--secondary-color); fill: var(--secondary-color); }
  50%      { stroke: var(--primary-color); fill: var(--primary-color); }
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`

const AnimatedSvg = styled('svg')({
    'display': 'block',
    'overflow': 'visible',
    'transformOrigin': 'center',
    'animation': `${spin} 8s linear infinite`,
    '& path': {
        strokeWidth: 0.6,
        vectorEffect: 'non-scaling-stroke',
        strokeDasharray: DASH,
        strokeDashoffset: DASH,
        fillOpacity: 0,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        animation: `
      ${drawStroke} ${CYCLE} ease-in-out infinite,
      ${fillIn} ${CYCLE} ease-in-out infinite,
      ${colorPulse} 3s ease-in-out infinite
    `
    },
    '& path:nth-of-type(1)': { animationDelay: `${0 * STAGGER}ms, ${0 * STAGGER}ms, 0s` },
    '& path:nth-of-type(2)': { animationDelay: `${1 * STAGGER}ms, ${1 * STAGGER}ms, 0s` },
    '& path:nth-of-type(3)': { animationDelay: `${2 * STAGGER}ms, ${2 * STAGGER}ms, 0s` },
    '& path:nth-of-type(4)': { animationDelay: `${3 * STAGGER}ms, ${3 * STAGGER}ms, 0s` },
    '& path:nth-of-type(5)': { animationDelay: `${4 * STAGGER}ms, ${4 * STAGGER}ms, 0s` },
    '& path:nth-of-type(6)': { animationDelay: `${5 * STAGGER}ms, ${5 * STAGGER}ms, 0s` },
    '& path:nth-of-type(7)': { animationDelay: `${6 * STAGGER}ms, ${6 * STAGGER}ms, 0s` },
    '& path:nth-of-type(8)': { animationDelay: `${7 * STAGGER}ms, ${7 * STAGGER}ms, 0s` }
})

const StreamLoader = ({ size = 18, ...props }: { size?: number }) => (
    <AnimatedSvg
        width={size}
        height={size}
        viewBox='0 0 17 17'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        {...props}>
        <path d='M15 10C15.9205 10 16.6667 9.25381 16.6667 8.33333C16.6667 7.41286 15.9205 6.66667 15 6.66667C14.0795 6.66667 13.3333 7.41286 13.3333 8.33333C13.3333 9.25381 14.0795 10 15 10Z' />
        <path d='M1.66667 10C2.58714 10 3.33333 9.25381 3.33333 8.33333C3.33333 7.41286 2.58714 6.66667 1.66667 6.66667C0.746192 6.66667 0 7.41286 0 8.33333C0 9.25381 0.746192 10 1.66667 10Z' />
        <path d='M8.33333 16.6667C9.25381 16.6667 10 15.9205 10 15C10 14.0795 9.25381 13.3333 8.33333 13.3333C7.41286 13.3333 6.66667 14.0795 6.66667 15C6.66667 15.9205 7.41286 16.6667 8.33333 16.6667Z' />
        <path d='M4.90831 10.5417L2.45831 12.9833C2.13331 13.3083 2.13331 13.8333 2.45831 14.1583C2.78331 14.4833 3.30831 14.4833 3.63331 14.1583L6.08331 11.7167C6.40831 11.4 6.40831 10.8667 6.08331 10.5417C5.75831 10.2167 5.23331 10.2167 4.90831 10.5417Z' />
        <path d='M3.67502 2.45002C3.35002 2.12502 2.82502 2.12502 2.50002 2.45002C2.17502 2.77502 2.17502 3.30002 2.50002 3.62502L4.94168 6.07502C5.26668 6.40002 5.79168 6.40002 6.12502 6.07502C6.44168 5.75002 6.44168 5.22502 6.11668 4.90002L3.67502 2.45002Z' />
        <path d='M11.7666 10.5417C11.4416 10.2167 10.9166 10.2167 10.5833 10.5417C10.2583 10.8667 10.2583 11.3917 10.5833 11.7167L13.0333 14.1667C13.3583 14.4917 13.8833 14.4917 14.2083 14.1667C14.5333 13.8417 14.5333 13.3167 14.2083 12.9917L11.7666 10.5417Z' />
        <path d='M11.7167 6.10834L14.2083 3.62501C14.5333 3.29168 14.5333 2.76668 14.2083 2.44168C13.8833 2.11668 13.3583 2.11668 13.0333 2.44168L10.5417 4.92501C10.2167 5.25001 10.2167 5.77501 10.5417 6.10834C10.8667 6.43334 11.3917 6.43334 11.7167 6.10834Z' />
        <path d='M8.33333 3.33333C9.25381 3.33333 10 2.58714 10 1.66667C10 0.746192 9.25381 0 8.33333 0C7.41286 0 6.66667 0.746192 6.66667 1.66667C6.66667 2.58714 7.41286 3.33333 8.33333 3.33333Z' />
    </AnimatedSvg>
)

export default StreamLoader
